import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Search } from 'lucide-react'
import { useEffect, useState } from 'react'
import { z } from 'zod'
import { playerSearchQuery } from '@/api/queries'
import { Input, Select } from '@/components/ui/controls'
import { Badge, DataCaveat, EmptyState, Skeleton } from '@/components/ui/feedback'
import { formatDate, formatInt, orDash } from '@/lib/utils'

const search = z.object({
  q: z.string().optional().catch(undefined),
  gender: z.enum(['M', 'F']).optional().catch(undefined),
})

export const Route = createFileRoute('/_app/players/')({
  validateSearch: search,
  component: PlayersPage,
})

function PlayersPage() {
  const { q = '', gender } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const [input, setInput] = useState(q)

  useEffect(() => {
    const t = setTimeout(() => {
      if (input !== q)
        navigate({ search: (p) => ({ ...p, q: input || undefined }), replace: true })
    }, 200)
    return () => clearTimeout(t)
  }, [input, q, navigate])

  const { data, isPending, isError } = useQuery({
    ...playerSearchQuery(q, gender),
    enabled: q.length >= 2,
  })

  return (
    <div className="mx-auto w-full max-w-4xl overflow-y-auto px-4 py-6">
      <h1 className="mb-4 font-semibold text-fg text-lg">Players</h1>

      <div className="mb-3 flex gap-2">
        <div className="relative flex-1">
          <Search
            className="-translate-y-1/2 pointer-events-none absolute top-1/2 left-3 size-4 text-subtle"
            aria-hidden
          />
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Search by surname — e.g. Djokovic, Swiatek, Alcaraz"
            className="pl-9"
            autoFocus
          />
        </div>
        <Select
          value={gender ?? ''}
          onChange={(e) =>
            navigate({
              search: (p) => ({
                ...p,
                gender: (e.target.value || undefined) as 'M' | 'F' | undefined,
              }),
              replace: true,
            })
          }
          options={[
            { value: 'M', label: 'Men' },
            { value: 'F', label: 'Women' },
          ]}
          placeholder="All"
          className="w-32"
        />
      </div>

      {/* Surprising enough to state up front rather than after an empty result. */}
      <DataCaveat className="mb-4">
        Players are stored as surname plus initial — there are no first names in this
        dataset. Searching “Novak” finds Dennis <em>Novak</em>, not Djokovic.
      </DataCaveat>

      {q.length < 2 ? (
        <EmptyState
          title="Search for a player"
          description="Type at least two letters of a surname."
        />
      ) : isPending ? (
        <div className="space-y-1">
          {Array.from({ length: 8 }, (_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
      ) : isError ? (
        <EmptyState title="Could not load players" />
      ) : data && data.items.length === 0 ? (
        <EmptyState
          title={`No player matches “${q}”`}
          description="Try a surname. First names are not present in this data."
        />
      ) : (
        <ul className="divide-y rounded-lg border">
          {data?.items.map((p) => (
            <li key={p.player_id}>
              <button
                type="button"
                onClick={() =>
                  navigate({ to: '/matches', search: { player_id: p.player_id } })
                }
                className="flex w-full items-center gap-4 px-4 py-3 text-left hover:bg-[var(--bg-hover)]"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-medium text-fg text-sm">
                      {p.display_name}
                    </span>
                    {p.identity_disputed && (
                      <Badge
                        tone="warn"
                        title="This name provably covers more than one real player"
                      >
                        disputed identity
                      </Badge>
                    )}
                  </div>
                  <p className="truncate text-subtle text-xs">
                    {orDash(p.country)} · {formatInt(p.n_matches)} matches ·{' '}
                    {formatDate(p.first_seen)} – {formatDate(p.last_seen)}
                  </p>
                </div>
                <dl className="hidden shrink-0 gap-4 text-right sm:flex">
                  {[
                    ['Elo', p.elo],
                    ['Hard', p.elo_hard],
                    ['Clay', p.elo_clay],
                    ['Grass', p.elo_grass],
                  ].map(([label, v]) => (
                    <div key={label as string}>
                      <dt className="text-[10px] text-subtle uppercase">{label}</dt>
                      <dd className="text-sm text-muted">
                        {typeof v === 'number' ? Math.round(v) : '—'}
                      </dd>
                    </div>
                  ))}
                </dl>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
